import React, { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Search, Upload, Loader2, FileType, X, Car, Zap, CheckCircle,
  XCircle, Tag, Brain, Cpu, Info, ChevronDown, ChevronUp,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AnalysisCard = ({ analysis, firmware_info }) => {
  const [expanded, setExpanded] = useState(true);
  const data = analysis || {};
  const info = firmware_info || {};

  const hasData = data.hw_id?.value || data.sw_id?.value || data.ecu_family_hint?.value || data.oem_hint?.value || data.other_ids?.length || info.hw_id || info.sw_id;
  if (!hasData) return null;

  const items = [];
  if (data.hw_id?.value) items.push({ label: 'HW ID', value: data.hw_id.value, confidence: data.hw_id.confidence });
  else if (info.hw_id) items.push({ label: 'HW ID', value: info.hw_id, confidence: null });

  if (data.sw_id?.value) items.push({ label: 'SW ID', value: data.sw_id.value, confidence: data.sw_id.confidence });
  else if (info.sw_id) items.push({ label: 'SW ID', value: info.sw_id, confidence: null });

  if (data.ecu_family_hint?.value) items.push({ label: 'ECU', value: data.ecu_family_hint.value, confidence: data.ecu_family_hint.confidence });
  else if (info.ecu_family) items.push({ label: 'ECU', value: info.ecu_family, confidence: null });

  if (data.oem_hint?.value) items.push({ label: 'OEM', value: data.oem_hint.value, confidence: data.oem_hint.confidence });
  else if (info.oem) items.push({ label: 'OEM', value: info.oem, confidence: null });

  if (data.other_ids?.length) {
    data.other_ids.forEach(item => items.push({ label: item.label, value: item.value, confidence: item.confidence }));
  }

  const summary = data.summary_es || data.summary || info.summary || '';

  return (
    <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg overflow-hidden">
      <button onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 p-4 text-left hover:bg-white/[0.02] transition-colors">
        <Brain className="w-5 h-5 text-blue-400 flex-shrink-0" />
        <span className="font-heading text-sm font-bold text-blue-400 flex-1">ANÁLISIS IA DEL FIRMWARE</span>
        {expanded ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
      </button>
      {expanded && (
        <div className="px-4 pb-4 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {items.map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 bg-surface rounded px-3 py-2">
                <span className="text-zinc-500 text-xs w-20 flex-shrink-0">{item.label}</span>
                <span className="text-white font-mono text-sm flex-1 truncate">{item.value}</span>
                {item.confidence !== null && item.confidence !== undefined && (
                  <span className={`text-xs font-mono flex-shrink-0 ${item.confidence >= 0.8 ? 'text-green-500' : item.confidence >= 0.5 ? 'text-yellow-500' : 'text-red-500'}`}>
                    {Math.round(item.confidence * 100)}%
                  </span>
                )}
              </div>
            ))}
          </div>
          {summary && (
            <p className="text-zinc-400 text-sm pt-2 border-t border-white/5 leading-relaxed">{summary}</p>
          )}
        </div>
      )}
    </div>
  );
};

const FileCheckerPage = () => {
  const { token, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [applyingId, setApplyingId] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [showProjectSelect, setShowProjectSelect] = useState(false);
  const [pendingVersion, setPendingVersion] = useState(null);

  // Vehicle context for better matching
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [ctxBrand, setCtxBrand] = useState('');
  const [ctxModel, setCtxModel] = useState('');
  const [ctxEcu, setCtxEcu] = useState('');

  useEffect(() => { axios.get(`${API}/catalog/brands`).then(r => setBrands(r.data.brands || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!ctxBrand) { setModels([]); setCtxModel(''); return; }
    axios.get(`${API}/catalog/models?brand=${encodeURIComponent(ctxBrand)}`).then(r => setModels(r.data.models || [])).catch(() => {});
    setCtxModel('');
  }, [ctxBrand]);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } });
      setProjects(res.data.projects || []);
    } catch { toast.error('Error al cargar vehículos'); }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) { setFile(droppedFile); setResult(null); }
  }, []);

  const handleDragOver = useCallback((e) => { e.preventDefault(); setDragOver(true); }, []);
  const handleDragLeave = useCallback(() => { setDragOver(false); }, []);

  const handleFileSelect = (e) => {
    const selected = e.target.files[0];
    if (selected) { setFile(selected); setResult(null); }
  };

  const checkFile = async () => {
    if (!file) return;
    setChecking(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (ctxBrand) formData.append('vehicle_brand', ctxBrand);
      if (ctxModel) formData.append('vehicle_model', ctxModel);
      if (ctxEcu) formData.append('ecu_type', ctxEcu);
      const response = await axios.post(`${API}/check-file`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(response.data);
    } catch { toast.error('Error al verificar archivo'); }
    finally { setChecking(false); }
  };

  const startApply = async (versionId) => {
    setPendingVersion(versionId);
    await fetchProjects();
    setShowProjectSelect(true);
  };

  const applyAutoSolution = async () => {
    if (!result || !pendingVersion || !selectedProject) return;
    setApplyingId(pendingVersion);
    try {
      const response = await axios.post(`${API}/auto-solution/apply`, null, {
        params: { solution_id: result.solution_id, version_id: pendingVersion, project_id: selectedProject },
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Solución aplicada - Ticket: ${response.data.ticket_number}`);
      await refreshUser();
      setShowProjectSelect(false);
      setPendingVersion(null);
      setSelectedProject('');
      navigate(`/tickets/${response.data.ticket_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aplicar solución');
    } finally { setApplyingId(null); }
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    setConfirmed(false);
    setShowProjectSelect(false);
    setPendingVersion(null);
    setSelectedProject('');
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto" data-testid="file-checker-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white">BUSCADOR DE ARCHIVOS</h1>
          <p className="text-zinc-400 mt-1">Arrastra tu archivo original para verificar si hay soluciones disponibles</p>
        </div>

        {/* Vehicle Context (optional, improves matching) */}
        <div className="mb-4 bg-surface border border-white/10 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Car className="w-4 h-4 text-primary" />
            <span className="text-zinc-300 text-sm font-medium">Datos del vehiculo (opcional, mejora la precision)</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <select value={ctxBrand} onChange={e => setCtxBrand(e.target.value)}
              className="bg-surface-highlight border border-white/10 rounded px-3 py-2 text-white text-sm" data-testid="ctx-brand">
              <option value="">Marca...</option>
              {brands.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
            <select value={ctxModel} onChange={e => setCtxModel(e.target.value)} disabled={!ctxBrand}
              className="bg-surface-highlight border border-white/10 rounded px-3 py-2 text-white text-sm disabled:opacity-40" data-testid="ctx-model">
              <option value="">Modelo...</option>
              {models.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <input value={ctxEcu} onChange={e => setCtxEcu(e.target.value.toUpperCase())} placeholder="ECU (ej: SIMOS18.1, EDC17C46...)"
              className="bg-surface-highlight border border-white/10 rounded px-3 py-2 text-white text-sm placeholder:text-zinc-600" data-testid="ctx-ecu" />
          </div>
        </div>

        {/* Warning */}
        <div className="mb-6 bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 flex items-start gap-3">
          <span className="text-yellow-500 text-lg flex-shrink-0">&#9888;</span>
          <div>
            <p className="text-yellow-400 font-medium text-sm">Solo archivos .bin originales sin encriptar</p>
            <p className="text-zinc-400 text-xs mt-1">Los archivos encriptados de herramientas Slave no son válidos. Utiliza únicamente lecturas en formato .bin de herramienta Master.</p>
          </div>
        </div>

        {/* Drop Zone */}
        <div onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}
          className={`relative border-2 border-dashed rounded-lg p-12 text-center transition-all duration-200 ${
            dragOver ? 'border-primary bg-primary/5 scale-[1.01]' : file ? 'border-green-500/50 bg-green-500/5' : 'border-white/20 bg-surface hover:border-white/40'
          }`} data-testid="file-drop-zone">
          {file ? (
            <div className="space-y-4">
              <FileType className="w-14 h-14 text-primary mx-auto" />
              <div>
                <p className="text-white text-lg font-medium">{file.name}</p>
                <p className="text-zinc-500 text-sm">{(file.size / 1024).toFixed(1)} KB</p>
              </div>
              <div className="flex gap-3 justify-center">
                <Button variant="ghost" onClick={reset} className="text-zinc-400 hover:text-white" data-testid="file-checker-reset-btn">
                  <X className="w-4 h-4 mr-2" />Cambiar archivo
                </Button>
                <Button onClick={checkFile} disabled={checking}
                  className="bg-primary hover:bg-primary/90 text-white font-heading uppercase tracking-wider px-8" data-testid="file-checker-search-btn">
                  {checking ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Analizando con IA...</>
                  ) : (
                    <><Search className="w-5 h-5 mr-2" />Buscar Solución</>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <Upload className="w-14 h-14 text-zinc-600 mx-auto" />
              <div>
                <p className="text-white text-lg">Arrastra tu archivo aquí</p>
                <p className="text-zinc-500 text-sm mt-1">o haz clic para seleccionar (solo .bin sin encriptar)</p>
              </div>
              <input type="file" onChange={handleFileSelect} className="hidden" id="checker-file" accept=".bin,.hex,.ori" />
              <Button variant="outline" onClick={() => document.getElementById('checker-file').click()}
                className="border-white/20 text-white hover:bg-white/10" data-testid="file-checker-select-btn">
                Seleccionar Archivo
              </Button>
            </div>
          )}
        </div>

        {/* Results */}
        {result && (
          <div className="mt-8 space-y-4" data-testid="file-checker-results">
            {result.match_found ? (
              <>
                {/* AI Analysis */}
                {result.analysis && <AnalysisCard analysis={result.analysis} />}

                {/* Confirmation Step */}
                {!confirmed ? (
                  <div className="bg-amber-500/5 border border-amber-500/30 rounded-lg p-6" data-testid="match-confirmation">
                    <div className="flex items-start gap-4">
                      <Cpu className="w-8 h-8 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h2 className="font-heading text-xl font-bold text-amber-400 mb-3">POSIBLE COINCIDENCIA</h2>
                        <p className="text-zinc-300 mb-4">Hemos encontrado una posible solución. Confirma si los datos coinciden con tu vehículo:</p>
                        <div className="bg-surface rounded-lg p-4 mb-4 space-y-2">
                          <div className="flex items-center gap-3">
                            <Car className="w-5 h-5 text-zinc-400" />
                            <span className="text-white text-lg font-bold">{result.vehicle}</span>
                          </div>
                          {result.ecu_type && (
                            <div className="flex items-center gap-3">
                              <Cpu className="w-5 h-5 text-zinc-400" />
                              <span className="text-zinc-300">ECU: <span className="text-white font-mono font-bold text-lg">{result.ecu_type}</span></span>
                            </div>
                          )}
                          {result.engine_code && (
                            <div className="flex items-center gap-3">
                              <Info className="w-5 h-5 text-zinc-400" />
                              <span className="text-zinc-300">Motor: <span className="text-white font-mono">{result.engine_code}</span></span>
                            </div>
                          )}
                          {result.match_type && (
                            <div className="flex items-center gap-2 mt-2">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-mono ${
                                result.match_type === 'exact' || result.match_type === 'exact_hash' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                              }`}>
                                {result.match_type === 'exact' || result.match_type === 'exact_hash' ? 'MATCH EXACTO' : `${result.match_type?.toUpperCase()} ${result.confidence ? Math.round(result.confidence * 100) + '%' : ''}`}
                              </span>
                              {result.ai_verified === true && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 font-mono flex items-center gap-1">
                                  <Brain className="w-3 h-3" />IA VERIFICADO
                                </span>
                              )}
                              {result.ai_verified === false && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-400 font-mono flex items-center gap-1">
                                  <Brain className="w-3 h-3" />IA NO CONFIRMADO
                                </span>
                              )}
                            </div>
                          )}
                          {result.ai_reason && (
                            <p className="text-zinc-400 text-xs mt-2 italic">{result.ai_reason}</p>
                          )}
                        </div>
                        <p className="text-amber-300 font-medium text-sm mb-4">¿Es esta la ECU de tu vehículo?</p>
                        <div className="flex gap-3">
                          <Button onClick={() => setConfirmed(true)}
                            className="bg-green-600 hover:bg-green-500 text-white px-8" data-testid="confirm-match-yes">
                            <CheckCircle className="w-4 h-4 mr-2" />Sí, es correcta
                          </Button>
                          <Button onClick={() => { setResult({...result, match_found: false, message: 'Match descartado por el usuario. Puedes crear un ticket manual.'}); }}
                            variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 px-8" data-testid="confirm-match-no">
                            <XCircle className="w-4 h-4 mr-2" />No, no es mi ECU
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Confirmed Match Header */}
                    <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-6">
                      <div className="flex items-start gap-4">
                        <CheckCircle className="w-8 h-8 text-green-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <h2 className="font-heading text-xl font-bold text-green-400">SOLUCIÓN CONFIRMADA</h2>
                            {result.match_type && (
                              <span className={`text-xs px-2 py-0.5 rounded-full font-mono ${
                                result.match_type === 'exact' || result.match_type === 'exact_hash' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                              }`}>
                                {result.match_type === 'exact' || result.match_type === 'exact_hash' ? 'EXACTO' : result.match_type?.toUpperCase()}
                              </span>
                            )}
                          </div>
                          <p className="text-zinc-300 mt-1">{result.message}</p>
                          <div className="flex items-center gap-2 mt-3">
                            <Car className="w-4 h-4 text-zinc-400" />
                            <span className="text-white">{result.vehicle}</span>
                            <span className="text-zinc-500">-</span>
                            <span className="text-zinc-400">{result.ecu_type}</span>
                            {result.engine_code && (<><span className="text-zinc-500">-</span><span className="text-zinc-400">{result.engine_code}</span></>)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Available Solutions */}
                    <div className="space-y-3">
                      <h3 className="font-heading text-sm text-zinc-500 uppercase tracking-wider">Soluciones disponibles</h3>
                      {result.available_solutions?.map((version) => (
                        <div key={version.id} className="bg-surface border border-white/10 rounded-lg p-5" data-testid={`solution-result-${version.id}`}>
                          <div className="flex flex-wrap gap-2 mb-3">
                            {version.solution_names?.map((name, i) => (
                              <span key={i} className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-3 py-1 rounded-full">
                                <Tag className="w-3 h-3" />{name}
                              </span>
                            ))}
                          </div>
                          {version.notes && <p className="text-zinc-400 text-sm mb-4">{version.notes}</p>}
                          <div className="flex items-center justify-between">
                            <span className="text-zinc-400 text-sm">Coste: <span className="font-mono text-lg text-primary font-bold">{version.credits_cost || version.price || 100} créditos</span></span>
                            <Button onClick={() => startApply(version.id)}
                              disabled={applyingId === version.id || (!user?.is_admin && (user?.credits || 0) < (version.credits_cost || version.price || 100))}
                              className="bg-primary hover:bg-primary/90 text-white" data-testid={`apply-solution-${version.id}`}>
                              {applyingId === version.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Zap className="w-4 h-4 mr-2" />Aplicar Solución</>}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {!user?.is_admin && (user?.credits || 0) < 50 && result.available_solutions?.some(v => (user?.credits || 0) < (v.credits_cost || v.price || 100)) && (
                      <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 text-center">
                        <p className="text-yellow-400 text-sm">Créditos insuficientes. Tu saldo: {user?.credits || 0} créditos.</p>
                        <Button variant="outline" size="sm" onClick={() => navigate('/credits')}
                          className="mt-2 border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10">Comprar Créditos</Button>
                      </div>
                    )}
                  </>
                )}

                {/* Other Matches */}
                {confirmed && result.all_matches?.length > 1 && (
                  <div className="bg-surface border border-white/10 rounded-lg p-4">
                    <h3 className="font-heading text-xs text-zinc-500 uppercase tracking-wider mb-2">Otros posibles matches</h3>
                    {result.all_matches.slice(1).map((m, i) => (
                      <div key={i} className="flex items-center gap-3 py-1.5 text-sm">
                        <span className="text-zinc-400">{m.vehicle}</span>
                        <span className="font-mono text-xs text-zinc-600">{m.match_type} ({Math.round(m.confidence * 100)}%)</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                {/* No Match */}
                <div className="bg-surface border border-white/10 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <XCircle className="w-8 h-8 text-zinc-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <h2 className="font-heading text-xl font-bold text-white">SIN RESULTADOS</h2>
                      <p className="text-zinc-400 mt-1">{result.message}</p>
                      <Button variant="outline" size="sm" onClick={() => navigate('/tickets/new')}
                        className="mt-4 border-white/20 text-white hover:bg-white/10">Crear Ticket Manual</Button>
                    </div>
                  </div>
                </div>

                {/* AI Analysis even without match */}
                {(result.analysis || result.firmware_info) && (
                  <AnalysisCard analysis={result.analysis} firmware_info={result.firmware_info} />
                )}
              </>
            )}
          </div>
        )}

        {/* Project Select Modal */}
        {showProjectSelect && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" data-testid="project-select-overlay">
            <div className="bg-surface border border-white/10 rounded-lg p-6 w-full max-w-md mx-4">
              <h3 className="font-heading text-lg font-bold text-white mb-4">SELECCIONA VEHÍCULO</h3>
              <p className="text-zinc-400 text-sm mb-4">Elige el vehículo al que aplicar la solución</p>
              {projects.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-zinc-500 mb-3">No tienes vehículos registrados</p>
                  <Button onClick={() => navigate('/projects/new')} className="bg-primary hover:bg-primary/90">Añadir Vehículo</Button>
                </div>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto mb-4">
                  {projects.map((p) => (
                    <button key={p.id} onClick={() => setSelectedProject(p.id)}
                      className={`w-full text-left p-3 rounded-md transition-colors ${
                        selectedProject === p.id ? 'bg-primary/10 border border-primary/30' : 'bg-surface-highlight border border-white/10 hover:border-white/20'
                      }`} data-testid={`project-option-${p.id}`}>
                      <p className="text-white font-medium">{p.vehicle_brand} {p.vehicle_model}</p>
                      <p className="text-zinc-500 text-sm">{p.vehicle_year} - {p.ecu_type}</p>
                    </button>
                  ))}
                </div>
              )}
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => { setShowProjectSelect(false); setPendingVersion(null); setSelectedProject(''); }}
                  className="flex-1 border-white/20 text-white hover:bg-white/10">Cancelar</Button>
                {projects.length > 0 && (
                  <Button onClick={applyAutoSolution} disabled={!selectedProject || applyingId}
                    className="flex-1 bg-primary hover:bg-primary/90" data-testid="confirm-apply-btn">
                    {applyingId ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Info */}
        <div className="mt-8 bg-surface border border-white/10 rounded-lg p-5">
          <h3 className="font-heading text-sm text-zinc-500 uppercase tracking-wider mb-3">¿Cómo funciona?</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
              <p className="text-zinc-400">Sube tu archivo original (.bin)</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
              <p className="text-zinc-400">La IA analiza el firmware y busca coincidencias inteligentes</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
              <p className="text-zinc-400">Si hay match, aplica la solución al instante</p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default FileCheckerPage;
